import _defineProperty from "@babel/runtime/helpers/defineProperty";
// Type declarations are used at compile-time as 'headers' only (does NOT include the files)

import { ResourceUtils } from "abweb/service/image/utils/ResourceUtils";
import { ImageType } from "abweb/service/image/api/ImageType";
import { LogUtils } from "abweb/utils/log/LogUtils";

/**
 * Generic container of ImageIdentifierInfo instances.
 */
export class ImageIdentifierLibrary {
  constructor() {
    _defineProperty(this, "_imageLibrary", {});
    _defineProperty(this, "_allowOverrides", false);
  }
  /**
   * Returns true if the namespace exists.
   * @param name The namespace to look for.
   * @returns {boolean} True if the namespace exists
   */
  hasNamespace(name) {
    return this._imageLibrary.hasOwnProperty(name);
  }
  getNamespaces() {
    return Object.keys(this._imageLibrary);
  }
  get allowOverrides() {
    return this._allowOverrides;
  }
  set allowOverrides(value) {
    this._allowOverrides = value;
  }
  getAllIdentifiers() {
    const infos = [];
    Object.keys(this._imageLibrary).forEach(ns => {
      Object.keys(this._imageLibrary[ns]).forEach(id => {
        infos.push(this._imageLibrary[ns][id]);
      });
    });
    return infos;
  }

  /**
   * Adds a namespace to the image manager.
   * @param name The namespace to add.
   * @throws Error Fails if namespace already exists
   */
  addNamespace(name) {
    if (ResourceUtils.isDynamicNamespace(name)) {
      throw new Error("Invalid reserved namespace specified: '" + name + "' name spaces cannot begin with or contain an exclamation point");
    }
    if (this._imageLibrary.hasOwnProperty(name)) {
      if (!this._allowOverrides) {
        throw new Error("Namespace already exists: '" + name + "'");
      }
    } else {
      this._imageLibrary[name] = {};
    }
  }

  /**
   * Adds a collection of image identifiers to the image manager. If the specified namespace doesn't exist, it is created. If
   * images contains an image already defined in the namespace, the existing definition will be overwritten.
   * @param namespace The namespace to add images to.
   * @param images The images being added.
   */
  addImageIdentifierInfos(images) {
    if (images !== null) {
      images.forEach(info => {
        this.addImageIdentifierInfo(info);
      });
    }
  }

  /**
   * Confirms that an image that is supposed to have scales has at least one. Throws an error if not.
   * @param info
   */
  validateScales(info) {
    if (info.type !== ImageType.FONT_BASED && (info.scales === null || info.scales.length === 0)) {
      throw new Error("Image of type " + info.type + " must have at least one scale: " + info.namespace + ": " + info.id);
    }
  }

  /**
   * Checks for a duplicated and throws an error if one is found.
   *
   * @param info
   */
  disallowDuplicates(info) {
    if (!this._allowOverrides && this.findImageIdentifierInfo(info.namespace, info.id)) {
      ImageIdentifierLibrary.LOG.error("Duplicate image registered for namespace " + info.namespace + ": " + info.id);
    }
  }

  /**
   * Adds a single image info. If the specified namespace doesn't exist, it is created. If the name
   * refers to an image already defined in the namespace, the existing definition will be overwritten.
   * @param namespace The namespace to add an image to.
   * @param name The name of the image to add.
   * @param value The path to the image.
   */
  addImageIdentifierInfo(info) {
    this.validateScales(info);
    this.disallowDuplicates(info);
    if (!this._imageLibrary.hasOwnProperty(info.namespace)) {
      this.addNamespace(info.namespace);
    }
    this._imageLibrary[info.namespace][info.id] = info;
  }

  /**
   * Will add the image identifier or replace an existing one with the same identifier.
   *
   * @param info
   */
  addOrReplaceImageIdentifierInfo(info) {
    this.validateScales(info);
    if (!this._imageLibrary.hasOwnProperty(info.namespace)) {
      this.addNamespace(info.namespace);
    }
    this._imageLibrary[info.namespace][info.id] = info;
  }

  /**
   * Finds and returns an image with the given id and namespace or null if not found
   * @param nameSpace
   * @param id
   * @returns ImageIdentifierInfo or null
   */
  findImageIdentifierInfo(nameSpace, id) {
    if (this.hasNamespace(nameSpace)) {
      if (this._imageLibrary[nameSpace].hasOwnProperty(id)) {
        return this._imageLibrary[nameSpace][id];
      }
    }
    return null;
  }

  /**
   * Finds an info using the combined namespace:id identifier.
   * @param identifier
   * @returns {ImageIdentifierInfo}
   */
  findImageIdentifierInfoByIdentifier(identifier) {
    const nameSpaceAndId = ResourceUtils.extractNameSpaceAndId(identifier);
    if (nameSpaceAndId.nameSpace == null) {
      throw new Error("No image info for identifier without nameSpace: " + identifier);
    }
    const info = this.findImageIdentifierInfo(nameSpaceAndId.nameSpace, nameSpaceAndId.id);
    return info;
  }

  /**
   * Clears the image library of all image identifier info.
   */
  removeAllInfo() {
    this._imageLibrary = {};
  }
}
_defineProperty(ImageIdentifierLibrary, "LOG", LogUtils.getLogger(__filename));
//# sourceMappingURL=ImageIdentifierLibrary.js.map
